// /_next/image fetches whatever host the query string names, re-encodes it and serves it
// from our own domain on our own transform bill, so this is an allowlist rather than a hint.
// Measured over every image and thumbnail of all 23,285 production products, plus the
// category and collection artwork embedded in them, and cross-checked against the hosts
// tienda.nelo.mx actually requests.
export const IMAGE_HOSTS = [
  "nelo-marketplace-prod.s3.amazonaws.com",
  "nelo-marketplace-prod.s3.us-east-1.amazonaws.com",
  "mkt-asset-campaign-upload.s3.amazonaws.com",
  "mkt-asset-campaign-upload.s3.us-east-1.amazonaws.com",
  "nelo-api-v2.s3.us-east-1.amazonaws.com",
  "images.nelo.mx",
  "cdn.shopify.com",
  "d83g1ecahmg39.cloudfront.net",
  "res.cloudinary.com",
  "i.ibb.co",
  // Fixture and maqueta-seeded feed content only; no production catalogue image uses them.
  "picsum.photos",
  "images.unsplash.com",
] as const

export interface RemoteImagePattern {
  protocol?: "http" | "https"
  hostname: string
  pathname?: string
}

// 105 of the 111 Mercado Libre images in the catalogue are stored as http, so this
// one host is matched on either protocol.
const MERCADO_LIBRE_HOST = "http2.mlstatic.com"
const PRODUCT_BUCKET = { hostname: "s3-us-west-2.amazonaws.com", pathname: "/im-prod-products-images/**" }
const LOOPBACK_HOST = "localhost"

// The loopback host is closed on Vercel rather than in production, so a local production
// build still renders the maqueta's own uploads.
export const imageRemotePatterns = ({ loopback }: { loopback: boolean }): RemoteImagePattern[] => [
  ...IMAGE_HOSTS.map((hostname) => ({ protocol: "https" as const, hostname })),
  { hostname: MERCADO_LIBRE_HOST },
  { protocol: "https", ...PRODUCT_BUCKET },
  ...(loopback ? [{ protocol: "http" as const, hostname: LOOPBACK_HOST }] : []),
]

const matchesPattern = (pattern: RemoteImagePattern, url: URL): boolean => {
  if (pattern.protocol && `${pattern.protocol}:` !== url.protocol) return false
  if (!pattern.protocol && url.protocol !== "https:" && url.protocol !== "http:") return false
  if (pattern.hostname !== url.hostname) return false
  if (!pattern.pathname || pattern.pathname === "/**") return true
  return url.pathname.startsWith(pattern.pathname.replace(/\*+$/, ""))
}

export const isOptimizableImageUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url)
    return imageRemotePatterns({ loopback: true }).some((pattern) => matchesPattern(pattern, parsed))
  } catch {
    return false
  }
}

export const isRenderableImageUrl = (url: string): boolean => (url.startsWith("/") && !url.startsWith("//")) || isOptimizableImageUrl(url)
